import {useLazyQuery, useMutation} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import {useDebounce} from '../../../../../_metronic/helpers'
import Select from 'react-select'
import {parseId} from '../../../helpers'
import {GET_ALL_USERS_FILTER} from '../../../gql/queries/usersQuery'
import {IResAllUsers} from '../../oneUser/oneUserTypes'
import {IResUpdatePolicyApi} from '../../../types/policies'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: (vars?: any) => any
  typeAssign: string
}

/** Llaves consumidas vía `t()` (el extractor no sigue wrappers, §17.12). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  labelAssign: {
    id: 'policies.detail.modal.assign.labelAssign',
    defaultMessage: 'Select the user to assign this policy',
  },
  labelOwner: {id: 'policies.detail.modal.assign.labelOwner', defaultMessage: 'Select the owner'},
  searchPlaceholder: {
    id: 'policies.detail.modal.assign.searchPlaceholder',
    defaultMessage: 'Search and select...',
  },
  loadingMembers: {
    id: 'policies.detail.modal.assign.loadingMembers',
    defaultMessage: 'Loading members...',
  },
})
export const ModalAssignAgentPolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  typeAssign,
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [gqlGetMembers, {data: dataUsers, loading: loadingUsers}] =
    useLazyQuery<IResAllUsers, {name: string, requireNpn: boolean}>(GET_ALL_USERS_FILTER)

  const [gqlCreateP, {data: dataUpdate, loading, error: errorUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success(t('policies.detail.statuses.toast.updated', 'Status updated successfully'))
      refetch({ id })
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(errMsg(dataUpdate.updatePolicy.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    let policyData: any

    if (typeAssign === 'ASSIGN_TO') {
      policyData = {assignedTo: agentId}
    } else {
      policyData = {user: agentId}
    }

    gqlCreateP({
      variables: {
        id: parseId(id),
        policyData,
      },
    })
  }

  useEffect(() => {
    if (errorUpdate) {
      toast.error(errMsg(errorUpdate.message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdate])

  const [agentId, setAgent] = useState<string | null>(null)
  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<{label: string; value: string}[]>([])

  useEffect(() => {
    if (!valueDeb) {
      setOptions([])
      return
    }
    if (!dataUsers) return
    setOptions(
      dataUsers.allUsers.edges.map(({node}) => ({
        label: `${node.firstName} ${node.lastName} - ${node.email}${
          node.npn ? ` (${node.npn})` : ''
        }`,
        value: node.id,
      }))
    )
  }, [dataUsers, valueDeb])

  useEffect(() => {
    if (!valueDeb) return
    gqlGetMembers({
      variables: {
        name: valueDeb,
        requireNpn: typeAssign === 'USER_TO',
      },
    })
  }, [gqlGetMembers, valueDeb, typeAssign])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        {typeAssign === 'ASSIGN_TO' && (
          <Modal.Title>
            <FormattedMessage id='policies.list.page.act.assignPolicy' defaultMessage='Assign policy' />
          </Modal.Title>
        )}
        {typeAssign === 'USER_TO' && (
          <Modal.Title>
            <FormattedMessage id='policies.detail.modal.assign.transferTitle' defaultMessage='Transfer policy' />
          </Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>
              {typeAssign === 'ASSIGN_TO'
                ? t('policies.detail.modal.assign.labelAssign', 'Select the user to assign this policy')
                : t('policies.detail.modal.assign.labelOwner', 'Select the owner')}
            </Form.Label>
            <Select
              isLoading={loadingUsers}
              loadingMessage={() => t('policies.detail.modal.assign.loadingMembers', 'Loading members...')}
              placeholder={t('policies.detail.modal.assign.searchPlaceholder', 'Search and select...')}
              options={options}
              inputValue={inputSelect}
              onInputChange={setInputSelect}
              onChange={(selectedOption) => {
                const userId = selectedOption?.value || 0
                console.log('Seleccionado:', userId)
                setAgent('' + parseId(userId))
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size='sm' variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={!agentId || loading}
        >
          {loading && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
